import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, MessageSquare, Twitter, Instagram, Youtube, Send, Facebook } from 'lucide-react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const socials = [
    {
      name: 'Telegram',
      icon: Send,
      url: 'https://t.me/ScavenjerHub',
      color: '#0088cc',
      hoverColor: '#006699'
    },
    {
      name: 'Twitter/X',
      icon: Twitter,
      url: 'https://x.com/ScavenjerGame',
      color: '#1DA1F2',
      hoverColor: '#0d8bd9'
    },
    {
      name: 'Instagram',
      icon: Instagram,
      url: 'https://www.instagram.com/scavenjer.game/',
      color: '#E4405F',
      hoverColor: '#d73651'
    },
    {
      name: 'YouTube',
      icon: Youtube,
      url: 'https://www.youtube.com/@ScavenjerFounder',
      color: '#FF0000',
      hoverColor: '#cc0000'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      url: 'https://www.facebook.com/profile.php?id=61563766544616',
      color: '#1877F2',
      hoverColor: '#166fe5'
    },
    {
      name: 'Discord',
      icon: MessageSquare,
      url: 'https://discord.com/invite/cp7jUyENEC',
      color: '#5865F2',
      hoverColor: '#4752c4'
    }
  ];

  const handleEmailClick = () => {
    window.open('mailto:pascaldiaz@scavenjer.com', '_blank');
  };

  const handleSocialClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-700 p-6 max-w-sm w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2 font-special">
                <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  Contact Us
                </span>
              </h3>
              <p className="text-gray-400 text-sm font-body">
                Get in touch with the Scavenjer team
              </p>
            </div>

            {/* Email Section */}
            <div className="mb-6">
              <button
                onClick={handleEmailClick}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-700 hover:to-gray-600 rounded-xl transition-all duration-300 border border-gray-600 hover:border-gray-500 group"
              >
                <div className="p-2 bg-gradient-to-r from-cyan-600 to-purple-600 rounded-lg">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-white font-semibold font-tech">Email Us</div>
                  <div className="text-gray-300 text-sm font-body">pascaldiaz@scavenjer.com</div>
                </div>
                <div className="text-gray-400 group-hover:text-gray-300 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              </button>
            </div>

            {/* Social Media Section */}
            <div>
              <h4 className="text-white font-semibold mb-3 text-center font-tech">
                Follow Us
              </h4>
              <div className="grid grid-cols-3 gap-3">
                {socials.map((social) => (
                  <button
                    key={social.name}
                    onClick={() => handleSocialClick(social.url)}
                    className="flex flex-col items-center gap-2 p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-xl transition-all duration-300 border border-gray-700 hover:border-gray-600 group"
                    style={{
                      borderColor: `${social.color}30`
                    }}
                  >
                    <div 
                      className="p-2 rounded-lg transition-colors duration-300"
                      style={{
                        backgroundColor: `${social.color}20`,
                        color: social.color
                      }}
                    >
                      <social.icon className="w-4 h-4" />
                    </div>
                    <span className="text-xs text-gray-300 font-body text-center leading-tight">
                      {social.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-700 text-center">
              <p className="text-xs text-gray-500 font-body">
                We'll get back to you as soon as possible!
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
